import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { ReportCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { ReportTypeCreate } from "../../../reportType/components/create/ReportTypeCreate";
import { ReportTypeCreateStore } from "../../../reportType/store";
import { InputCodeEditor } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type ReportCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  reportTypeSelectedValue?: { displayId: string; id: string } | null;
  onReportTypeSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onReportTypeChange?: (value: string) => void;
  reportTypeOptions?: { displayId: string; id: string }[];
  reportTypeError?: string;
  templateSelectedValue?: { displayId: string; id: string } | null;
  onTemplateChange?: (value: string) => void;
  template: string;
  templateError?: string;
  constantsSelectedValue?: { displayId: string; id: string } | null;
  onConstantsChange?: (value: string) => void;
  constants: string;
  constantsError?: string;
  descriptionSelectedValue?: { displayId: string; id: string } | null;
  onDescriptionChange?: (value: string) => void;
  description: string;
  descriptionError?: string;
  reportCreateStore: ReportCreateStore;
};

export const ReportCreateForm: React.FC<ReportCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name = "",
    nameError,
    onNameChange = () => {},
    onReportTypeSelected = () => {},
    reportTypeSelectedValue = null,
    reportTypeOptions = [],
    reportTypeError,
    onReportTypeChange = () => {},
    template = "",
    templateError,
    onTemplateChange = () => {},
    constants = "",
    constantsError,
    onConstantsChange = () => {},
    description = "",
    descriptionError,
    onDescriptionChange = () => {},
    reportCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="Report"
            mode="create"
            values={{}}
            options={{ reportType: reportTypeOptions }}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "name":
                  onNameChange?.(valor);
                  break;
                case "reportType":
                  onReportTypeSelected?.(valor);
                  break;
                case "template":
                  onTemplateChange?.(valor);
                  break;
                case "constants":
                  onConstantsChange?.(valor);
                  break;
                case "description":
                  onDescriptionChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="null"
            errorText={reportCreateStore.getCardErrors("null")}
            title={langStore.groupReportLabel}
            collapsible={false}
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name}
                error={Boolean(nameError)}
                warning={
                  !nameError && Boolean(reportCreateStore.warnings["name"])
                }
                helperText={
                  nameError || reportCreateStore.warnings["name"]?.message
                }
                label={langStore.ReportNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.ReportNameDescription}
              />
              <InputReference
                id="reportType"
                label={langStore.ReportReportTypeLabel}
                error={Boolean(reportTypeError)}
                helperText={
                  reportTypeError ||
                  reportCreateStore.warnings["reportType"]?.message
                }
                selectedValue={reportTypeSelectedValue}
                onChange={(value: string) => onReportTypeChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onReportTypeSelected(value)
                }
                options={reportTypeOptions}
                description={langStore.ReportReportTypeDescription}
                canCreate={rootStore.authStore.hasPermissions(
                  "reportType_create"
                )}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) =>
                      new ReportTypeCreateStore(rootStore)
                    }
                    renderForm={(store) => (
                      <ReportTypeCreate
                        reportTypeCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewReportType"
                    cancelMethod="cancelNewReportType"
                  />
                )}
              />

              <InputCodeEditor
                value={template}
                error={Boolean(templateError)}
                helperText={
                  templateError ||
                  reportCreateStore.warnings["template"]?.message
                }
                label={langStore.ReportTemplateLabel}
                onChange={(value: string) => onTemplateChange(value)}
                language="nunjucks"
                description={langStore.ReportTemplateDescription}
              />

              <InputCodeEditor
                value={constants}
                error={Boolean(constantsError)}
                helperText={
                  constantsError ||
                  reportCreateStore.warnings["constants"]?.message
                }
                label={langStore.ReportConstantsLabel}
                onChange={(value: string) => onConstantsChange(value)}
                language="json"
                description={langStore.ReportConstantsDescription}
              />

              <InputText
                value={description}
                error={Boolean(descriptionError)}
                warning={
                  !descriptionError &&
                  Boolean(reportCreateStore.warnings["description"])
                }
                helperText={
                  descriptionError ||
                  reportCreateStore.warnings["description"]?.message
                }
                label={langStore.ReportDescriptionLabel}
                onChange={(value: string) => onDescriptionChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.ReportDescriptionDescription}
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
